import {useEffect, useRef, useContext, useState} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "components/maps/utils/leafletCompat";

import WasteSelectorTypes from "components/waste-icons/WasteSelectorTypes";
import AuthContext from "context/AuthContext";
import {leafletLang} from "./leafletLang";
import {faPen} from "components/svgs/fontawesome";
import {ZoneModel} from "./ZoneModels";
import { CARTO_TILE_URL, CARTO_TILE_OPTIONS } from 'core/mapsConfig';


const MapZonesJS = ({assets, zones, zone = {...ZoneModel}, polylines}) => {
    const {panel, user} = useContext(AuthContext);
    const mapRef = useRef(null);
    const drawnItemsRef = useRef(new L.FeatureGroup());
    const editZoneRef = useRef(new L.FeatureGroup());
    const drawControlRef = useRef(null);
    const [firstZone, setFirstZone] = useState({...zone});

    const size = {
        topBase: 0.0005,
        bottomBase: 0.0003,
        height: 0.0004,
    };

    const styleZone = {
        color: "#8d9091",
        weight: 4,
        fillColor: "#a3a3a3",
        fillOpacity: 0.5,
    };

    const styleEditZone = {
        color: "#3388ff",
        weight: 4,
        fillColor: "#3388ff",
        fillOpacity: 0.5,
    };

    useEffect(() => {
        setFirstZone({...zone});
        const initializeMap = () => {
            const validCenter = Array.isArray(panel?.coords) && panel.coords.length === 2 && Number.isFinite(panel.coords[0]) && Number.isFinite(panel.coords[1]);
            if (!validCenter) return;
            if (!mapRef.current) {
                mapRef.current = L.map("modaleditzone-map", {
                    center: panel.coords,
                    zoom: 14,
                });

                L.tileLayer(CARTO_TILE_URL, CARTO_TILE_OPTIONS).addTo(mapRef.current);

                drawnItemsRef.current.addTo(mapRef.current);
                editZoneRef.current.addTo(mapRef.current);

                const drawControl = new L.Control.Draw({
                    edit: {
                        featureGroup: editZoneRef.current,
                        edit: true,
                        remove: true,
                    },
                    draw: {
                        polyline: false,
                        rectangle: false,
                        circle: false,
                        marker: false,
                        circlemarker: false,
                        polygon: {
                            shapeOptions: styleEditZone,
                        },
                    },
                });
                drawControlRef.current = drawControl;
                mapRef.current.addControl(drawControl);

                L.drawLocal = leafletLang[user.language];

                mapRef.current.on(L.Draw.Event.DRAWSTART, handleStart);
                mapRef.current.on(L.Draw.Event.CREATED, handleCreated);
                mapRef.current.on(L.Draw.Event.EDITED, handleEdited);
                mapRef.current.on(L.Draw.Event.DELETED, handleDeleted);
                mapRef.current.on("zoomend", handleZoom);
                customizeDrawButtons();
            }

            addAssetsToMap();
            addZonesToMap();
            addEditZoneToMap();
        };

        initializeMap();

        return () => {
            if (mapRef.current) {
                mapRef.current.off(L.Draw.Event.DRAWSTART, handleStart);
                mapRef.current.off(L.Draw.Event.CREATED, handleCreated);
                mapRef.current.off(L.Draw.Event.EDITED, handleEdited);
                mapRef.current.off(L.Draw.Event.DELETED, handleDeleted);
                mapRef.current.off("zoomend", handleZoom);
                if (mapRef.current.off) mapRef.current.off();
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [assets, zones, user.language, zone]);

    const addAssetsToMap = () => {
        if (!mapRef.current) return;
        assets.forEach((asset) => {
            if (!Number.isFinite(asset.lat) || !Number.isFinite(asset.lng)) return;
            const {color, fillColor, borderColor} = WasteSelectorTypes.getStyle(asset.waste_type);
            const circle = L.circle([asset.lat, asset.lng], {
                color: borderColor,
                fillColor: fillColor,
                fillOpacity: 0.8,
                weight: 1,
                radius: calculateRadius(mapRef.current.getZoom()), // Radio inicial basado en zoom
            }).addTo(mapRef.current);
            asset.circle = circle; // Guardamos la referencia al círculo en el objeto asset
        });
    };

    const addZonesToMap = () => {
        if (!mapRef.current) return;
        drawnItemsRef.current.clearLayers();
        zones.forEach((zone) => {
            if (!zone || !Array.isArray(zone.polylines) || !zone.polylines.length) return;
            const coords = zone.polylines
                .map((poly) => {
                    if (poly && Number.isFinite(poly.lat) && Number.isFinite(poly.lng)) return [poly.lat, poly.lng];
                    return null;
                })
                .filter(Boolean);
            if (!coords.length) return;
            const polygon = L.polygon(coords, styleZone).addTo(mapRef.current);
            polygon.bindPopup(`<b>${zone.name}</b><br>${zone.description}`);
            drawnItemsRef.current.addLayer(polygon);
        });
    };
    const addEditZoneToMap = () => {
        if (!mapRef.current) return;
        editZoneRef.current.clearLayers(); // Clear existing edit zones
        if (!zone || !Array.isArray(zone.polylines) || !zone.polylines.length) return;
        const coords = zone.polylines
            .map((poly) => (poly && Number.isFinite(poly.lat) && Number.isFinite(poly.lng) ? [poly.lat, poly.lng] : null))
            .filter(Boolean);
        if (!coords.length) return;
        const editPolygon = L.polygon(coords, styleEditZone).addTo(mapRef.current);
        editPolygon.bindPopup(`<b>${zone.name}</b><br>${zone.description}`);
        editZoneRef.current.addLayer(editPolygon);
    };

    const handleStart = () => {
        editZoneRef.current.clearLayers();
        drawnItemsRef.current.clearLayers();
        addZonesToMap();
    };

    const handleCreated = (e) => {
        const {layerType, layer} = e;
        if (layerType === "polygon") {
            const coordinates = layer.getLatLngs();
            polylines.current = coordinates[0];
            editZoneRef.current.clearLayers();
            editZoneRef.current.addLayer(layer);
        }
    };

    const handleEdited = (e) => {
        const layers = e.layers;
        layers.eachLayer((layer) => {
            if (layer instanceof L.Polygon) {
                const coordinates = layer.getLatLngs();
                polylines.current = coordinates[0];
            }
        });
    };

    const handleDeleted = () => {
        polylines.current = firstZone.polylines;
    };

    const handleZoom = () => {
        if (!mapRef.current) return;
        const zoomLevel = mapRef.current.getZoom();
        assets.forEach((asset) => {
            if (!asset.circle) return;
            const newRadius = calculateRadius(zoomLevel);
            asset.circle.setRadius(newRadius);
        });
    };

    const calculateRadius = (zoomLevel) => {
        switch (zoomLevel) {
            case 15:
                return 32; // Radio para el zoom nivel 15
            case 16:
                return 22; // Radio para el zoom nivel 16
            case 17:
                return 12; // Radio para el zoom nivel 17
            case 18:
                return 5; // Radio para el zoom nivel 18
            default:
                return 50; // Valor por defecto
        }
    };

    const customizeDrawButtons = () => {
        const polygonButton = document.querySelector(".leaflet-draw-draw-polygon");
        if (polygonButton) {
            const iconContainer = document.createElement("div");
            iconContainer.innerHTML = faPen;
            polygonButton.innerHTML = "";
            polygonButton.appendChild(iconContainer.firstChild);
            // Style
            polygonButton.style.display = "flex";
            polygonButton.style.justifyContent = "center";
            polygonButton.style.alignItems = "center";
            polygonButton.style.backgroundImage = "none";
        }
    };

    return <div id="modaleditzone-map" style={{height: "calc(100vh - 220px)", width: "100%"}}></div>;
};

export default MapZonesJS;
